/**
 * KAPE AI Learning Experience Builder — Feature-flagged wizard.
 * Route: /kcis/admin/kape/ai-builder
 *
 * Explicit accessible colors (no inherited/opacity-based text). The
 * page renders inside the dark KcisAdminShell, so top-level headings
 * use white/near-white; every white content card explicitly sets dark
 * slate text.
 *
 * Blueprint state distinguishes:
 *   • no-blueprint  (never generated)      → "Generate Blueprint"
 *   • generating                            → progress banner
 *   • generated + valid content             → blueprint review + Approve
 *   • generated + INVALID (empty content)   → error banner + Retry, no Approve
 *   • failed / load error                   → error banner + Retry, no Approve
 *
 * "Approve Blueprint" is disabled unless blueprint contains real
 * content. The backend enforces the same rule server-side; front-end
 * disable is only a UX hint.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import KcisAdminShell from "./KcisAdminShell";
import PreviewAsStudentButton from "./PreviewAsStudentButton";

const BASE = "/kcis/admin/kape/ai-builder";

// Explicit accessible palette (used as inline style so no Tailwind
// dark-mode inheritance can wash them out).
const C = {
  // On dark shell
  headingOnDark:      "#FFFFFF",
  bodyOnDark:         "#DDD3EA",
  mutedOnDark:        "#A79DB4",
  ctaBg:              "#5B21B6",  // KCIS purple
  ctaText:            "#FFFFFF",
  ctaBorder:          "#7C3AED",
  // On white card
  cardHeading:        "#0F172A",  // slate-900
  cardBody:           "#334155",  // slate-700
  cardMuted:          "#64748B",  // slate-500
  cardLabel:          "#1E293B",  // slate-800
  cardBorder:         "#CBD5E1",  // slate-300
  cardBorderStrong:   "#94A3B8",  // slate-400
  inputText:          "#0F172A",
  placeholder:        "#94A3B8",
  focus:              "#7C3AED",
  danger:             "#B91C1C",  // red-700
  dangerBg:           "#FEF2F2",  // red-50
  warnBg:             "#FFFBEB",  // amber-50
  warnText:           "#92400E",  // amber-900
  successBg:          "#ECFDF5",
  successText:        "#065F46",
  chipSelectedBg:     "#5B21B6",
  chipSelectedText:   "#FFFFFF",
  chipUnselectedBg:   "#FFFFFF",
  chipUnselectedText: "#0F172A",
  chipUnselectedBorder: "#94A3B8",
};

const EXPERIENCE_LABELS = {
  university_course: "University Course",
  certification_module: "Certification Module",
  continuing_education: "Continuing-Education Course",
  workshop: "Workshop",
  coaching_program: "Coaching Program",
};

const LENGTH_LABELS = {
  mini: "Mini Experience",
  approx_1_hour: "Approximately 1 Hour",
  half_day: "Half-Day Workshop",
  full_day: "Full-Day Workshop",
  multi_day: "Multi-Day Workshop",
  multi_module_course: "Multi-Module Course",
  multi_week_program: "Multi-Week Program",
  custom: "Custom",
};

// ═════════════ Page ═════════════
export default function AILearningExperienceBuilder() {
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState("");
  const [courses, setCourses] = useState(null);
  const [showIntake, setShowIntake] = useState(false);
  const [params, setParams] = useSearchParams();
  const activeId = params.get("id") || "";

  const loadConfig = useCallback(async () => {
    setConfigError("");
    try {
      const { data } = await api.get(`${BASE}/config`, { timeout: 15000 });
      setConfig(data);
    } catch (e) {
      const s = e?.response?.status;
      if (s !== 401) setConfigError(`Unable to load builder config (HTTP ${s || "network"}).`);
    }
  }, []);

  const loadCourses = useCallback(async () => {
    try {
      const { data } = await api.get(`${BASE}/courses`, { timeout: 15000 });
      setCourses(data.items || []);
    } catch { /* 401 handled globally */ }
  }, []);

  useEffect(() => { loadConfig(); loadCourses(); }, [loadConfig, loadCourses]);

  if (activeId) {
    const auto = params.get("auto") === "1";
    return (
      <KcisAdminShell>
        <BuilderDetail
          courseId={activeId}
          autoStart={auto}
          onAutoStartConsumed={() => {
            const next = new URLSearchParams(params);
            next.delete("auto");
            setParams(next);
          }}
          onExit={() => { setParams({}); loadCourses(); }}
        />
      </KcisAdminShell>
    );
  }

  return (
    <KcisAdminShell>
      <div className="max-w-5xl mx-auto px-8 py-10" data-testid="ai-builder-page">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl" style={{ color: C.headingOnDark }}>
              KAPE AI Course Builder
            </h1>
            <p className="mt-2 text-sm max-w-2xl" style={{ color: C.bodyOnDark }}>
              One intake. One Build Course action. KAPE generates the blueprint,
              lessons, slides, narration scripts, activities, assessments and
              workbook — then hands you the completed draft to Preview as Student
              and publish.
            </p>
          </div>
          {config?.enabled && (
            <button
              onClick={() => setShowIntake(true)}
              className="rounded-full px-5 h-11 text-sm font-semibold"
              style={{ background: C.ctaBg, color: C.ctaText, border: `1px solid ${C.ctaBorder}` }}
              data-testid="ai-builder-create-btn"
            >
              Create Course
            </button>
          )}
        </div>

        {configError && (
          <div className="rounded-lg p-4 text-sm mb-4"
               style={{ background: C.warnBg, color: C.warnText, border: "1px solid #FCD34D" }}
               data-testid="ai-builder-config-error">
            {configError}
          </div>
        )}

        {config && !config.enabled && (
          <div className="rounded-xl p-5 text-sm"
               style={{ background: "#F1F5F9", color: C.cardBody, border: `1px solid ${C.cardBorder}` }}
               data-testid="ai-builder-disabled-notice">
            <div className="font-semibold mb-1" style={{ color: C.cardHeading }}>
              Feature not enabled in this environment
            </div>
            The KAPE AI Course Builder is behind the
            <code className="px-1 py-0.5 rounded mx-1" style={{ background: "#E2E8F0" }}>KAPE_AI_BUILDER_ENABLED</code>
            flag. Set it to <code>true</code> in the environment to enable this workflow.
            The Advanced Course Editor continues to work regardless of this flag.
          </div>
        )}

        {config?.enabled && (
          <CoursesList courses={courses} onOpen={(id) => setParams({ id })} />
        )}

        {showIntake && config?.enabled && (
          <IntakeDialog
            config={config}
            onClose={() => setShowIntake(false)}
            onCreated={(id) => {
              setShowIntake(false);
              // `auto=1` tells BuilderDetail to immediately kick off
              // Stage 1 blueprint generation without a second click.
              setParams({ id, auto: "1" });
              loadCourses();
            }}
          />
        )}
      </div>
    </KcisAdminShell>
  );
}

function CoursesList({ courses, onOpen }) {
  if (courses === null) {
    return <div className="text-sm" style={{ color: C.mutedOnDark }} data-testid="ai-builder-loading">Loading drafts…</div>;
  }
  if (courses.length === 0) {
    return (
      <div className="rounded-xl p-8 text-center text-sm"
           style={{ background: "#F8FAFC", color: C.cardBody, border: `1px dashed ${C.cardBorder}` }}
           data-testid="ai-builder-empty">
        No AI Builder drafts yet. Click <strong style={{ color: C.cardHeading }}>Create Learning Experience</strong> to start.
      </div>
    );
  }
  return (
    <div className="rounded-xl overflow-hidden" style={{ background: "#FFFFFF", border: `1px solid ${C.cardBorder}` }} data-testid="ai-builder-list">
      {courses.map((c) => (
        <button
          key={c.id}
          onClick={() => onOpen(c.id)}
          className="w-full text-left p-4 hover:bg-slate-50"
          style={{ borderBottom: `1px solid #E2E8F0` }}
          data-testid={`ai-builder-course-${c.id}`}
        >
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold" style={{ color: C.cardHeading }}>{c.title}</div>
              <div className="text-[12px] mt-0.5" style={{ color: C.cardMuted }}>
                {EXPERIENCE_LABELS[c.experience_type] || c.experience_type || "—"}
                {" · "}
                {LENGTH_LABELS[c.desired_length] || c.desired_length || "—"}
                {" · Status: "}{c.status}
                {c.blueprint_status ? ` · Blueprint: ${c.blueprint_status}` : ""}
              </div>
            </div>
            <div className="text-[11px]" style={{ color: C.cardMuted }}>
              Updated {(c.updated_at || "").slice(0, 10)}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// Only render profiles that have a real human-readable display name.
// display_name > full_name > slug. If none, drop the row.
function instructorDisplayName(p) {
  const ident = (p && p.identity) || {};
  const name = (ident.display_name || ident.full_name || "").trim();
  if (name) return name;
  // Slug is a URL-safe fallback (e.g., "dr-christy-vonta"); prettify.
  const slug = ((p && p.slug) || "").trim();
  if (!slug) return "";
  return slug.replaceAll("-", " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function instructorHasVoice(p) {
  return !!((p && p.voice && p.voice.voice_id || "").trim());
}

function normalizeInstructorList(raw) {
  const items = Array.isArray(raw) ? raw : [];
  const seen = new Set();
  const out = [];
  for (const p of items) {
    if (!p || p.active === false) continue;         // exclude inactive
    if (!p.id || seen.has(p.id)) continue;          // dedupe by unique id
    const name = instructorDisplayName(p);
    if (!name) continue;                            // require display name
    seen.add(p.id);
    out.push({
      id: p.id,
      name,
      voice_ready: instructorHasVoice(p),
      is_default: !!p.is_default,
    });
  }
  out.sort((a, b) => {
    // Default profile first, then alphabetical.
    if (a.is_default && !b.is_default) return -1;
    if (b.is_default && !a.is_default) return 1;
    return a.name.localeCompare(b.name);
  });
  return out;
}

// ═════════════ Intake dialog ═════════════
function IntakeDialog({ config, onClose, onCreated }) {
  const [experienceType, setExperienceType] = useState("");
  const [title, setTitle] = useState("");
  const [learnerOutcome, setLearnerOutcome] = useState("");
  const [sourceInformation, setSourceInformation] = useState("");
  const [desiredLength, setDesiredLength] = useState("approx_1_hour");
  const [creditHours, setCreditHours] = useState("10");
  const [instructorId, setInstructorId] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advanced, setAdvanced] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [instructors, setInstructors] = useState(null);      // null=loading, []=empty, [...]=list
  const [instructorLoadError, setInstructorLoadError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/kcis/admin/instructor-profiles",
                                        { timeout: 15000 });
        if (!alive) return;
        setInstructors(normalizeInstructorList(data?.items));
      } catch (e) {
        if (!alive) return;
        const s = e?.response?.status;
        setInstructorLoadError(`Instructor profiles could not be loaded (HTTP ${s || "network"}).`);
        setInstructors([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const creditHoursValue = parseFloat(creditHours);
  const creditHoursValid = Number.isFinite(creditHoursValue) && creditHoursValue > 0;
  const canSubmit =
    !!experienceType && title.trim().length > 0
    && learnerOutcome.trim().length >= 8 && creditHoursValid && !submitting;
  const isCoaching = experienceType === "coaching_program";
  const hoursLabel = isCoaching ? "Program Hours *" : "Credit Hours *";

  const selectedInstructor = useMemo(() => {
    const list = instructors || [];
    return list.find((x) => x.id === instructorId) || null;
  }, [instructorId, instructors]);

  const instructorSummaryLabel = useMemo(() => {
    if (!selectedInstructor) return "— Not selected —";
    return selectedInstructor.name
      + (selectedInstructor.voice_ready ? " — Voice ready" : " — Voice not connected");
  }, [selectedInstructor]);

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true); setErr("");
    try {
      const { data } = await api.post(`${BASE}/intake`, {
        experience_type: experienceType,
        title: title.trim(),
        learner_outcome: learnerOutcome.trim(),
        source_information: sourceInformation.trim(),
        desired_length: desiredLength,
        credit_hours: creditHoursValue,
        primary_instructor_id: instructorId || "",
        advanced_options: advanced,
      });
      onCreated(data.course.id);
    } catch (e) {
      const s = e?.response?.status;
      const d = e?.response?.data?.detail;
      const msg = (d && typeof d === "object" && d.message)
        || (typeof d === "string" ? d : "")
        || `Could not create draft (HTTP ${s || "network"}).`;
      setErr(msg);
    } finally { setSubmitting(false); }
  }

  return (
    <div
      role="dialog" aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.72)" }}
      data-testid="ai-builder-intake-dialog"
    >
      <div className="w-full max-w-2xl rounded-2xl shadow-xl max-h-[92vh] overflow-y-auto"
           style={{ background: "#FFFFFF", color: C.cardBody, border: `1px solid ${C.cardBorder}` }}>
        <div className="p-6" style={{ borderBottom: `1px solid #E2E8F0` }}>
          <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: C.focus }}>Step 1 · Intake</div>
          <h2 className="text-xl font-display mt-1" style={{ color: C.cardHeading }}>Create a Learning Experience</h2>
        </div>

        <div className="p-6 space-y-6">
          {/* 1) Experience type */}
          <Field label="Experience Type *" hint="Choose one — this steers the blueprint, defaults, and required components.">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="ai-builder-experience-types">
              {(config.experience_types || []).map((t) => {
                const selected = experienceType === t;
                return (
                  <button
                    key={t} type="button"
                    onClick={() => setExperienceType(t)}
                    aria-pressed={selected}
                    className="text-left rounded-xl p-3 text-sm font-medium transition-colors"
                    style={{
                      background: selected ? C.chipSelectedBg : C.chipUnselectedBg,
                      color: selected ? C.chipSelectedText : C.chipUnselectedText,
                      border: `2px solid ${selected ? C.chipSelectedBg : C.chipUnselectedBorder}`,
                      boxShadow: selected ? "0 2px 6px rgba(91,33,182,0.35)" : "none",
                    }}
                    data-testid={`ai-builder-type-${t}`}
                    data-selected={selected ? "true" : "false"}
                  >
                    {selected && <span aria-hidden="true" style={{ marginRight: 6 }}>✓</span>}
                    {EXPERIENCE_LABELS[t] || t}
                  </button>
                );
              })}
            </div>
          </Field>

          {/* 2) Title */}
          <Field label="Working Title *">
            <TextInput value={title} onChange={setTitle}
                       placeholder="e.g., NC Cosmetic Arts Instructor Exam Prep"
                       testid="ai-builder-title" />
          </Field>

          {/* 3) Learner outcome */}
          <Field label="Learner Outcome *"
                 hint="What should the learner know, do, complete, or change by the end?">
            <TextArea value={learnerOutcome} onChange={setLearnerOutcome} rows={3}
                      placeholder="One clear sentence describing the change."
                      testid="ai-builder-outcome" />
          </Field>

          {/* 4) Source information */}
          <Field label="Source Information"
                 hint="Paste key content, notes, or a rough outline (optional).">
            <TextArea value={sourceInformation} onChange={setSourceInformation} rows={4}
                      placeholder="Paste source material here."
                      testid="ai-builder-source" />
          </Field>

          {/* 5) Desired length */}
          <Field label="Desired Length *">
            <select
              value={desiredLength}
              onChange={(e) => setDesiredLength(e.target.value)}
              className="w-full h-11 rounded-xl px-3 text-sm font-medium"
              style={{ background: "#FFFFFF", color: C.inputText,
                       border: `1px solid ${C.cardBorderStrong}`,
                       outline: "none" }}
              data-testid="ai-builder-length"
            >
              {(config.desired_lengths || []).map((l) => (
                <option key={l} value={l}>{LENGTH_LABELS[l] || l}</option>
              ))}
            </select>
          </Field>

          {/* 6) Credit Hours — owner-entered, AI must not override */}
          <Field
            label={hoursLabel}
            hint={isCoaching
              ? "Total program hours. AI will size the modules and lessons to this value."
              : "Positive whole or decimal number. AI will size the modules, lessons, activities, and assessments to fit these hours."}
          >
            <input
              type="number" min="0.5" step="0.5"
              value={creditHours}
              onChange={(e) => setCreditHours(e.target.value)}
              className="w-full h-11 rounded-xl px-3 text-sm font-medium"
              style={{ background: "#FFFFFF", color: C.inputText,
                       border: `1px solid ${creditHoursValid ? C.cardBorderStrong : "#FCA5A5"}`,
                       outline: "none" }}
              data-testid="ai-builder-credit-hours"
            />
            {!creditHoursValid && creditHours !== "" && (
              <div className="text-xs mt-1" style={{ color: C.danger }}>
                Enter a positive whole or decimal number.
              </div>
            )}
          </Field>

          {/* 6) Instructor */}
          <Field
            label="Instructor"
            hint="Written content can be generated for any active instructor. Voiceover requires an approved instructor voice."
          >
            {instructors === null && (
              <div className="text-sm py-2" style={{ color: C.cardMuted }}
                   data-testid="ai-builder-instructor-loading">
                Loading instructor profiles…
              </div>
            )}
            {instructors !== null && instructorLoadError && (
              <div className="rounded-lg p-2 text-xs"
                   style={{ background: C.dangerBg, color: C.danger, border: "1px solid #FCA5A5" }}
                   data-testid="ai-builder-instructor-error">
                {instructorLoadError}
              </div>
            )}
            {instructors !== null && !instructorLoadError && instructors.length === 0 && (
              <div className="rounded-lg p-3 text-sm"
                   style={{ background: "#F1F5F9", color: C.cardBody, border: `1px solid ${C.cardBorder}` }}
                   data-testid="ai-builder-instructor-empty">
                No active instructor profiles are available. Add one from
                <strong style={{ color: C.cardHeading }}> Instructor AI Profiles</strong> in the left navigation.
              </div>
            )}
            {instructors !== null && instructors.length > 0 && (
              <>
                <select
                  value={instructorId}
                  onChange={(e) => setInstructorId(e.target.value)}
                  className="w-full h-11 rounded-xl px-3 text-sm font-medium"
                  style={{ background: "#FFFFFF", color: C.inputText,
                           border: `1px solid ${C.cardBorderStrong}`,
                           outline: "none" }}
                  data-testid="ai-builder-instructor"
                >
                  <option value="">— Select an instructor —</option>
                  {instructors.map((i) => (
                    <option key={i.id} value={i.id}
                            data-testid={`ai-builder-instructor-option-${i.id}`}>
                      {i.name} — {i.voice_ready ? "Voice ready" : "Voice not connected"}
                    </option>
                  ))}
                </select>
                {selectedInstructor && !selectedInstructor.voice_ready && (
                  <div className="mt-2 rounded-lg p-2 text-xs"
                       style={{ background: C.warnBg, color: C.warnText,
                                border: "1px solid #FCD34D" }}
                       data-testid="ai-builder-voice-unavailable-notice">
                    Voiceover unavailable until an approved instructor voice is connected.
                  </div>
                )}
              </>
            )}
          </Field>

          {/* Advanced */}
          <div style={{ paddingTop: 8, borderTop: `1px solid #E2E8F0` }}>
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              className="text-xs font-semibold"
              style={{ color: C.focus }}
              data-testid="ai-builder-advanced-toggle"
            >
              {advancedOpen ? "▾" : "▸"} Advanced Options
            </button>
            {advancedOpen && (
              <div className="mt-3 space-y-4" data-testid="ai-builder-advanced-panel">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {["audience","prerequisites","academic_level","tone","passing_score",
                     "delivery_format","citation_requirements","accessibility_preferences"].map((k) => (
                    <label key={k} className="block">
                      <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.cardLabel }}>
                        {k.replaceAll("_"," ")}
                      </span>
                      <input
                        type="text"
                        value={advanced[k] || ""}
                        onChange={(e) => setAdvanced({ ...advanced, [k]: e.target.value })}
                        className="mt-1 w-full h-9 rounded-lg px-2 text-sm"
                        style={{ background: "#FFFFFF", color: C.inputText,
                                 border: `1px solid ${C.cardBorderStrong}` }}
                        data-testid={`ai-builder-adv-${k}`}
                      />
                    </label>
                  ))}
                </div>

                {/* License categories — shown only for regulated types.
                    Ensures examples/assignments stay within the
                    learner's own licensure scope. */}
                {(experienceType === "certification_module"
                  || experienceType === "continuing_education") && (
                  <LicenseCategoryPicker
                    value={advanced.license_categories || []}
                    onChange={(next) => setAdvanced({ ...advanced, license_categories: next })}
                  />
                )}

                {/* Official / regulatory sources — shown for regulated types. */}
                {(experienceType === "certification_module"
                  || experienceType === "continuing_education") && (
                  <RegulatorySources
                    value={advanced.regulatory_sources || []}
                    onChange={(next) => setAdvanced({ ...advanced, regulatory_sources: next })}
                  />
                )}
              </div>
            )}
          </div>

          {/* Review summary — required so the owner sees exactly what
              will be submitted. */}
          <div className="rounded-xl p-4"
               style={{ background: "#F8FAFC", border: `1px solid ${C.cardBorder}` }}
               data-testid="ai-builder-review-summary">
            <div className="text-[11px] uppercase tracking-wider font-bold mb-2" style={{ color: C.focus }}>
              Review before submitting
            </div>
            <SummaryRow label="Experience Type"
                        value={experienceType ? (EXPERIENCE_LABELS[experienceType] || experienceType) : "— Not selected —"}
                        missing={!experienceType}
                        testid="ai-builder-summary-type" />
            <SummaryRow label="Working Title"
                        value={title.trim() || "— Not entered —"}
                        missing={!title.trim()}
                        testid="ai-builder-summary-title" />
            <SummaryRow label="Learner Outcome"
                        value={learnerOutcome.trim().length >= 8
                          ? (learnerOutcome.trim().length > 90
                              ? learnerOutcome.trim().slice(0,90) + "…"
                              : learnerOutcome.trim())
                          : "— Please describe (at least one sentence) —"}
                        missing={learnerOutcome.trim().length < 8}
                        testid="ai-builder-summary-outcome" />
            <SummaryRow label="Desired Length"
                        value={LENGTH_LABELS[desiredLength] || desiredLength}
                        testid="ai-builder-summary-length" />
            <SummaryRow label={isCoaching ? "Program Hours" : "Credit Hours"}
                        value={creditHoursValid ? `${creditHoursValue}` : "— Not entered —"}
                        missing={!creditHoursValid}
                        testid="ai-builder-summary-hours" />
            <SummaryRow label="Instructor"
                        value={instructorSummaryLabel}
                        testid="ai-builder-summary-instructor" />
          </div>

          {err && (
            <div className="rounded-lg p-3 text-sm"
                 style={{ background: C.dangerBg, color: C.danger,
                          border: `1px solid #FCA5A5` }}
                 data-testid="ai-builder-intake-error">
              {err}
            </div>
          )}
        </div>

        <div className="p-6 flex justify-end gap-2" style={{ borderTop: `1px solid #E2E8F0` }}>
          <button
            type="button" onClick={onClose} disabled={submitting}
            className="rounded-lg px-4 py-2 text-sm font-semibold"
            style={{ background: "#FFFFFF", color: C.cardHeading,
                     border: `1px solid ${C.cardBorderStrong}`,
                     opacity: submitting ? 0.5 : 1 }}
            data-testid="ai-builder-intake-cancel"
          >
            Cancel
          </button>
          <button
            type="button" onClick={submit} disabled={!canSubmit}
            className="rounded-lg px-4 py-2 text-sm font-semibold"
            style={{ background: canSubmit ? C.ctaBg : "#94A3B8",
                     color: C.ctaText, opacity: canSubmit ? 1 : 0.65,
                     cursor: canSubmit ? "pointer" : "not-allowed" }}
            data-testid="ai-builder-intake-submit"
          >
            {submitting ? "Creating…" : "Build My Learning Experience"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold" style={{ color: C.cardLabel }}>{label}</span>
      {hint && <div className="text-xs mt-1 mb-1" style={{ color: C.cardMuted }}>{hint}</div>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function TextInput({ value, onChange, placeholder, testid }) {
  return (
    <input
      type="text" value={value} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full h-11 rounded-xl px-3 text-sm"
      style={{ background: "#FFFFFF", color: C.inputText,
               border: `1px solid ${C.cardBorderStrong}`,
               outline: "none" }}
      data-testid={testid}
    />
  );
}

function TextArea({ value, onChange, rows, placeholder, testid }) {
  return (
    <textarea
      value={value} onChange={(e) => onChange(e.target.value)}
      rows={rows} placeholder={placeholder}
      className="w-full rounded-xl px-3 py-2 text-sm"
      style={{ background: "#FFFFFF", color: C.inputText,
               border: `1px solid ${C.cardBorderStrong}`,
               outline: "none" }}
      data-testid={testid}
    />
  );
}

function SummaryRow({ label, value, missing, testid }) {
  return (
    <div className="flex items-baseline gap-3 py-1">
      <div className="text-[11px] uppercase tracking-wider font-bold w-40 shrink-0"
           style={{ color: C.cardMuted }}>{label}</div>
      <div className="text-sm font-medium"
           style={{ color: missing ? C.danger : C.cardHeading }}
           data-testid={testid}>{value}</div>
    </div>
  );
}

// ═════════════ License-category picker (regulated types only) ═════════════
const LICENSE_CATEGORIES = [
  { key: "cosmetology",   label: "Cosmetology" },
  { key: "esthetics",     label: "Esthetics" },
  { key: "manicuring",    label: "Manicuring" },
  { key: "natural_hair",  label: "Natural Hair Care" },
];

function LicenseCategoryPicker({ value, onChange }) {
  const selected = new Set(value || []);
  function toggle(k) {
    const next = new Set(selected);
    if (next.has(k)) next.delete(k); else next.add(k);
    onChange(Array.from(next));
  }
  return (
    <div data-testid="ai-builder-license-categories">
      <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.cardLabel }}>
        License-category tracks
      </div>
      <div className="text-xs mt-1 mb-2" style={{ color: C.cardMuted }}>
        Examples, assignments, and demonstrations stay within each selected
        licensure category. Learners are not asked to perform procedures
        outside their own licence.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {LICENSE_CATEGORIES.map((c) => {
          const on = selected.has(c.key);
          return (
            <button
              key={c.key} type="button" onClick={() => toggle(c.key)}
              className="text-left rounded-lg p-2 text-sm font-medium"
              style={{
                background: on ? C.chipSelectedBg : C.chipUnselectedBg,
                color: on ? C.chipSelectedText : C.chipUnselectedText,
                border: `2px solid ${on ? C.chipSelectedBg : C.chipUnselectedBorder}`,
              }}
              data-testid={`ai-builder-license-${c.key}`}
              data-selected={on ? "true" : "false"}
            >
              {on && <span aria-hidden="true" style={{ marginRight: 4 }}>✓</span>}
              {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ═════════════ Regulatory sources (regulated types only) ═════════════
function RegulatorySources({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  function update(i, patch) {
    const next = items.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  }
  function add() {
    onChange([...items, { title: "", url: "", accessed_on: "", notes: "" }]);
  }
  function remove(i) {
    const next = items.slice();
    next.splice(i, 1);
    onChange(next);
  }
  return (
    <div data-testid="ai-builder-regulatory-sources">
      <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.cardLabel }}>
        Official or regulatory sources
      </div>
      <div className="text-xs mt-1 mb-2" style={{ color: C.cardMuted }}>
        Attach at least one authoritative source (statute, board rule, exam
        bulletin, CE-provider approval letter). AI does not have internet
        access; only these sources support regulatory claims.
      </div>
      <div className="space-y-2">
        {items.map((s, i) => (
          <div key={i} className="rounded-lg p-3"
               style={{ background: "#F8FAFC", border: `1px solid ${C.cardBorder}` }}
               data-testid={`ai-builder-source-${i}`}>
            <input
              type="text" placeholder="Source title (required)"
              value={s.title || ""}
              onChange={(e) => update(i, { title: e.target.value })}
              className="w-full h-9 rounded px-2 text-sm mb-2"
              style={{ background: "#FFFFFF", color: C.inputText,
                       border: `1px solid ${C.cardBorderStrong}` }}
              data-testid={`ai-builder-source-title-${i}`}
            />
            <input
              type="text" placeholder="URL (optional)"
              value={s.url || ""}
              onChange={(e) => update(i, { url: e.target.value })}
              className="w-full h-9 rounded px-2 text-sm mb-2"
              style={{ background: "#FFFFFF", color: C.inputText,
                       border: `1px solid ${C.cardBorderStrong}` }}
              data-testid={`ai-builder-source-url-${i}`}
            />
            <input
              type="text" placeholder="Access / publish date"
              value={s.accessed_on || ""}
              onChange={(e) => update(i, { accessed_on: e.target.value })}
              className="w-full h-9 rounded px-2 text-sm mb-2"
              style={{ background: "#FFFFFF", color: C.inputText,
                       border: `1px solid ${C.cardBorderStrong}` }}
              data-testid={`ai-builder-source-date-${i}`}
            />
            <textarea
              rows={2} placeholder="Notes (optional)"
              value={s.notes || ""}
              onChange={(e) => update(i, { notes: e.target.value })}
              className="w-full rounded px-2 py-1 text-sm"
              style={{ background: "#FFFFFF", color: C.inputText,
                       border: `1px solid ${C.cardBorderStrong}` }}
              data-testid={`ai-builder-source-notes-${i}`}
            />
            <div className="text-right mt-1">
              <button type="button" onClick={() => remove(i)}
                      className="text-xs" style={{ color: C.danger }}
                      data-testid={`ai-builder-source-remove-${i}`}>
                Remove source
              </button>
            </div>
          </div>
        ))}
        <button type="button" onClick={add}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold"
                style={{ background: "#FFFFFF", color: C.cardHeading,
                         border: `1px solid ${C.cardBorderStrong}` }}
                data-testid="ai-builder-source-add">
          + Add source
        </button>
      </div>
    </div>
  );
}

// ═════════════ Detail view ═════════════
function blueprintHasContent(bp) {
  if (!bp) return false;
  const hasSummary = typeof bp.summary === "string" && bp.summary.trim().length > 0;
  const hasModules = Array.isArray(bp.module_structure) && bp.module_structure.length > 0;
  const hasObjectives = Array.isArray(bp.learning_objectives) && bp.learning_objectives.length > 0;
  // Blueprint is considered "valid" only when it has at least a
  // non-empty module outline AND either a summary or objectives.
  return hasModules && (hasSummary || hasObjectives);
}

function BuilderDetail({ courseId, autoStart = false, onAutoStartConsumed, onExit }) {
  const [course, setCourse] = useState(null);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [loadError, setLoadError] = useState("");
  const [moduleResults, setModuleResults] = useState([]);
  const [showEditIntake, setShowEditIntake] = useState(false);
  const [config, setConfig] = useState(null);
  const [initialGenerationAttempted, setInitialGenerationAttempted] = useState(false);
  // Track transient "generating for the first time" state so the
  // banner + button label reflect the true workflow phase.
  const [firstTimeGenerating, setFirstTimeGenerating] = useState(false);
  const [firstTimeError, setFirstTimeError] = useState(false);

  const refresh = useCallback(async () => {
    setLoadError("");
    try {
      const { data } = await api.get(`/kcis/admin/kape/courses/${courseId}`);
      setCourse(data.course);
    } catch (e) {
      const s = e?.response?.status;
      setLoadError(`Unable to load draft (HTTP ${s || "network"}).`);
    }
  }, [courseId]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    api.get(`${BASE}/config`).then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  async function generateBlueprint(confirmOverwrite = false, { firstTime = false } = {}) {
    if (busy === "blueprint") return;   // guard against double-clicks
    setBusy("blueprint"); setErr("");
    if (firstTime) { setFirstTimeGenerating(true); setFirstTimeError(false); }
    try {
      const { data } = await api.post(
        `${BASE}/${courseId}/generate-blueprint`,
        { confirm_overwrite_approved: confirmOverwrite },
        { timeout: 120000 },
      );
      setCourse(data.course);
      const bp = (data.course || {}).blueprint;
      if (!blueprintHasContent(bp)) {
        setErr("The AI provider returned an empty blueprint. Please retry.");
        if (firstTime) setFirstTimeError(true);
      } else {
        toast.success("Blueprint generated. Review it below.");
      }
    } catch (e) {
      const s = e?.response?.status;
      const d = e?.response?.data?.detail;
      if (s === 409 && d?.code === "blueprint_already_approved") {
        if (window.confirm("Regenerating will overwrite the approved blueprint. Continue?")) {
          return generateBlueprint(true);
        }
      } else {
        setErr((d && d.message) || `Blueprint failed (HTTP ${s || "network"}).`);
        if (firstTime) setFirstTimeError(true);
      }
    } finally {
      setBusy("");
      if (firstTime) setFirstTimeGenerating(false);
    }
  }

  // Auto-kick blueprint generation when the intake wizard hands us
  // `autoStart=true`. The course document already exists (created by
  // POST /intake); a refresh cannot cause a duplicate draft, and the
  // effect guards itself against double-firing.
  useEffect(() => {
    if (!course) return;
    if (!autoStart) return;
    if (initialGenerationAttempted) return;
    const bp = course.blueprint || {};
    if (bp.generated_at) {
      // Already generated at some point — do not auto-regenerate.
      setInitialGenerationAttempted(true);
      onAutoStartConsumed && onAutoStartConsumed();
      return;
    }
    setInitialGenerationAttempted(true);
    onAutoStartConsumed && onAutoStartConsumed();
    generateBlueprint(false, { firstTime: true });
  }, [course, autoStart, initialGenerationAttempted]);

  async function approveBlueprint() {
    setBusy("approve"); setErr("");
    try {
      const { data } = await api.post(`${BASE}/${courseId}/approve-blueprint`, {});
      setCourse(data.course);
      toast.success("Blueprint approved. You can now generate full content.");
    } catch (e) {
      const s = e?.response?.status;
      const d = e?.response?.data?.detail;
      setErr((d && d.message) || `Could not approve blueprint (HTTP ${s || "network"}).`);
    } finally { setBusy(""); }
  }

  const [fullDraftJob, setFullDraftJob] = useState(null);
  const [startingJob, setStartingJob] = useState(false);

  async function pollJob() {
    try {
      const { data } = await api.get(`${BASE}/${courseId}/full-draft-job`);
      setFullDraftJob(data.job || null);
    } catch { /* silent */ }
  }

  useEffect(() => {
    // Poll every 6s while a job is active. Refreshing the page picks
    // up the same job because status is persisted.
    let alive = true;
    async function tick() {
      if (!alive) return;
      await pollJob();
    }
    tick();
    const id = setInterval(tick, 6000);
    return () => { alive = false; clearInterval(id); };
  }, [courseId]);

  const [completing, setCompleting] = useState(false);
  const [publishing, setPublishing] = useState(false);

  async function buildCourse() {
    if (startingJob) return;
    setStartingJob(true); setErr("");
    try {
      const { data } = await api.post(
        `${BASE}/${courseId}/build-course`, {},
      );
      setFullDraftJob({ id: data.job_id, status: data.status,
                        completed_lessons: 0, total_lessons: 0 });
      toast.success(data.reused
        ? "Continuing existing Build Course job."
        : "Build Course started — generating your course.");
    } catch (e) {
      const s = e?.response?.status;
      const d = e?.response?.data?.detail;
      setErr((d && d.message) || `Could not start Build Course (HTTP ${s || "network"}).`);
    } finally { setStartingJob(false); }
  }

  async function generateContent() {
    if (startingJob) return;                     // dup-click guard
    setStartingJob(true); setErr("");
    try {
      const { data } = await api.post(
        `${BASE}/${courseId}/start-full-draft-job`, {},
      );
      // Immediate 202 → poll for progress. Do NOT hold the browser
      // request open.
      setFullDraftJob({ id: data.job_id, status: data.status,
                        completed_lessons: 0, total_lessons: 0 });
      toast.success(data.reused ? "Continuing existing job." : "Full-draft job queued.");
    } catch (e) {
      const s = e?.response?.status;
      const d = e?.response?.data?.detail;
      setErr((d && d.message) || `Could not start job (HTTP ${s || "network"}).`);
    } finally { setStartingJob(false); }
  }

  async function resumeJob() {
    if (startingJob) return;
    setStartingJob(true); setErr("");
    try {
      const { data } = await api.post(`${BASE}/${courseId}/resume-full-draft-job`, {});
      setFullDraftJob({ id: data.job_id, status: data.status });
      toast.success("Resuming generation.");
    } catch (e) {
      setErr("Could not resume job. Please try again.");
    } finally { setStartingJob(false); }
  }

  async function refreshCourse() {
    try {
      const { data } = await api.get(`${BASE}/courses/${courseId}`);
      if (data?.course) setCourse(data.course);
    } catch { /* silent */ }
  }

  async function completeMissingAssets() {
    if (completing) return;
    setCompleting(true); setErr("");
    try {
      const { data } = await api.post(
        `${BASE}/${courseId}/complete-missing-assets`, {});
      toast.success(
        `Completed missing assets. Created ${data.assignments_created} `
        + `assignments, ${data.module_quizzes_created} module quizzes, `
        + `${data.final_exam_created} final exam.`);
      await refreshCourse();
    } catch (e) {
      setErr("Could not complete missing assets. Please try again.");
    } finally { setCompleting(false); }
  }

  async function publishCourse() {
    if (publishing) return;
    setPublishing(true); setErr("");
    try {
      const price = Number((course?.intake?.price) || 0);
      const sell = price > 0 && (course?.intake?.publish_as === "public");
      const { data } = await api.post(`${BASE}/${courseId}/publish`, {
        price, sell, allow_partial: false,
      });
      toast.success(`Published to ${data.product.catalog_section}.`);
      await refreshCourse();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.blockers) {
        setErr("Publish blocked: " + detail.blockers.join(" · "));
      } else {
        setErr("Could not publish. Please try again.");
      }
    } finally { setPublishing(false); }
  }

  if (loadError) {
    return (
      <div className="max-w-5xl mx-auto px-8 py-10" data-testid="ai-builder-detail">
        <button onClick={onExit} className="text-xs mb-3" style={{ color: C.mutedOnDark }}
                data-testid="ai-builder-back">← Back to drafts</button>
        <div className="rounded-lg p-4 text-sm"
             style={{ background: C.dangerBg, color: C.danger, border: `1px solid #FCA5A5` }}
             data-testid="ai-builder-detail-load-error">
          {loadError}
        </div>
      </div>
    );
  }
  if (!course) {
    return (
      <div className="max-w-5xl mx-auto px-8 py-10 text-sm" style={{ color: C.mutedOnDark }}
           data-testid="ai-builder-detail-loading">Loading…</div>
    );
  }

  const intake = course.intake || {};
  const bp = course.blueprint || {};
  const bpApproved = bp.status === "approved";
  const bpValid = blueprintHasContent(bp);
  const bpEverGenerated = !!bp.generated_at;
  // Verification blockers must be resolved before approval.
  const verifItems = Array.isArray(bp.verification_required) ? bp.verification_required : [];
  const unresolvedBlockers = verifItems.filter(
    (f) => f && f.severity === "blocker"
      && !(f.acknowledged || f.sourced || f.resolved),
  );
  const canApprove = bpValid && !bpApproved && busy !== "approve"
    && unresolvedBlockers.length === 0;

  // Button label follows the workflow phase strictly:
  //   • never generated → "Generate Blueprint"
  //   • generated once, empty/invalid or failed → "Retry Blueprint Generation"
  //   • valid content present → "Regenerate Blueprint"
  const generateBtnLabel =
    busy === "blueprint"
      ? (firstTimeGenerating ? "Generating blueprint…" : "Generating…")
      : !bpEverGenerated
        ? "Generate Blueprint"
        : bpValid
          ? "Regenerate Blueprint"
          : "Retry Blueprint Generation";

  return (
    <div className="max-w-5xl mx-auto px-8 py-10" data-testid="ai-builder-detail">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <button onClick={onExit} className="text-xs mb-2 hover:underline"
                  style={{ color: C.mutedOnDark }} data-testid="ai-builder-back">
            ← Back to drafts
          </button>
          <h1 className="font-display text-2xl" style={{ color: C.headingOnDark }}>
            {intake.title || "(Untitled learning experience)"}
          </h1>
          <div className="text-xs mt-1" style={{ color: C.bodyOnDark }}>
            {EXPERIENCE_LABELS[intake.experience_type] || intake.experience_type || "—"}
            {" · "}
            {LENGTH_LABELS[intake.desired_length] || intake.desired_length || "—"}
            {intake.credit_hours ? ` · ${intake.credit_hours} ${
              intake.experience_type === "coaching_program" ? "program hours" : "credit hours"
            }` : ""}
            {" · Status: "}{course.status}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div data-testid="ai-builder-preview-student-wrap">
            <PreviewAsStudentButton
              courseId={courseId}
              label="Preview as Student"
              variant="ghost"
              size="md"
              testIdSuffix="ai-builder-header"
            />
          </div>
          <a
            href={`/kcis/admin/university/course-builder/${courseId}`}
            target="_blank" rel="noreferrer"
            className="rounded-lg px-3 py-2 text-xs font-semibold"
            style={{ background: "#FFFFFF", color: "#0F172A",
                     border: "1px solid #FFFFFF" }}
            data-testid="ai-builder-open-full-editor"
          >
            Advanced Course Editor
          </a>
          <button
            onClick={() => setShowEditIntake(true)}
            className="rounded-lg px-3 py-2 text-xs font-semibold"
            style={{ background: "rgba(255,255,255,0.12)", color: "#FFFFFF",
                     border: "1px solid rgba(255,255,255,0.5)" }}
            data-testid="ai-builder-edit-intake"
          >
            Edit Course Details
          </button>
        </div>
      </div>

      {err && (
        <div className="rounded-lg p-3 text-sm mb-4"
             style={{ background: C.dangerBg, color: C.danger, border: `1px solid #FCA5A5` }}
             data-testid="ai-builder-detail-error">{err}</div>
      )}

      {/* Course Built Summary + Post-build actions (visible whenever
          the course has any generated lessons or completed job) */}
      <CourseBuiltSummary
        course={course}
        job={fullDraftJob}
        onCompleteMissing={completeMissingAssets}
        onPublish={publishCourse}
        completing={completing}
        publishing={publishing}
        courseId={courseId}
      />

      {/* Stage 1 — Blueprint */}
      <section className="rounded-xl p-6 mb-6"
               style={{ background: "#FFFFFF", border: `1px solid ${C.cardBorder}`, color: C.cardBody }}
               data-testid="ai-builder-blueprint-section">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: C.focus }}>Stage 1 · Blueprint</div>
            <h2 className="text-lg mt-1" style={{ color: C.cardHeading }}>Course blueprint</h2>
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={buildCourse}
              disabled={startingJob}
              className="rounded-lg px-4 py-2 text-sm font-semibold"
              style={{ background: startingJob ? "#94A3B8" : "#10B981",
                       color: "#0F172A",
                       border: "1px solid #059669",
                       cursor: startingJob ? "not-allowed" : "pointer",
                       opacity: startingJob ? 0.7 : 1 }}
              title="One action: blueprint → auto-approve → full draft → sync"
              data-testid="ai-builder-build-course"
            >
              {startingJob ? "Starting Build…" : "Build Course"}
            </button>
            <button
              onClick={() => generateBlueprint(false)}
              disabled={busy === "blueprint"}
              className="rounded-lg px-3 py-2 text-xs font-semibold"
              style={{ background: "#F8FAFC", color: "#0F172A",
                       border: "1px solid #CBD5E1",
                       cursor: busy === "blueprint" ? "not-allowed" : "pointer",
                       opacity: busy === "blueprint" ? 0.7 : 1 }}
              title="Advanced: regenerate blueprint only"
              data-testid="ai-builder-generate-blueprint"
            >
              {generateBtnLabel}
            </button>
            {bpValid && !bpApproved && (
              <button
                onClick={approveBlueprint}
                disabled={!canApprove}
                title={unresolvedBlockers.length
                  ? `Approval blocked by ${unresolvedBlockers.length} verification item(s).`
                  : ""}
                className="rounded-lg px-3 py-2 text-sm font-semibold"
                style={{ background: "#FFFFFF", color: canApprove ? C.cardHeading : C.cardMuted,
                         border: `1px solid ${C.cardBorderStrong}`,
                         opacity: canApprove ? 1 : 0.5,
                         cursor: canApprove ? "pointer" : "not-allowed" }}
                data-testid="ai-builder-approve-blueprint"
              >
                Approve Blueprint
              </button>
            )}
            {bpApproved && (
              <span className="rounded-full px-3 py-1 text-[11px] font-bold"
                    style={{ background: C.successBg, color: C.successText,
                             border: `1px solid #86EFAC` }}
                    data-testid="ai-builder-blueprint-approved-badge">Approved</span>
            )}
          </div>
        </div>

        {/* State banners — distinguish first-time vs. subsequent flows */}
        {(firstTimeGenerating || (busy === "blueprint" && !bpEverGenerated)) && (
          <div className="rounded-lg p-3 text-sm mb-3"
               style={{ background: "#EFF6FF", color: "#1E40AF", border: "1px solid #93C5FD" }}
               data-testid="ai-builder-blueprint-generating">
            <strong>Saving intake and generating blueprint…</strong> This may take up to a minute. Please leave this page open.
          </div>
        )}
        {busy === "blueprint" && bpEverGenerated && !firstTimeGenerating && (
          <div className="rounded-lg p-3 text-sm mb-3"
               style={{ background: "#EFF6FF", color: "#1E40AF", border: "1px solid #93C5FD" }}
               data-testid="ai-builder-blueprint-generating">
            Regenerating blueprint…
          </div>
        )}
        {firstTimeError && !bpEverGenerated && (
          <div className="rounded-lg p-3 text-sm mb-3"
               style={{ background: C.dangerBg, color: C.danger, border: "1px solid #FCA5A5" }}
               data-testid="ai-builder-blueprint-first-time-failed">
            Blueprint generation did not complete. Click <strong>Retry Blueprint Generation</strong> above. Your intake has been saved — no duplicate draft was created.
          </div>
        )}
        {busy !== "blueprint" && !bpEverGenerated && !firstTimeError && !autoStart && (
          <div className="rounded-lg p-3 text-sm"
               style={{ background: "#F1F5F9", color: C.cardBody, border: `1px solid ${C.cardBorder}` }}
               data-testid="ai-builder-blueprint-empty">
            No blueprint has been generated yet. Click <strong style={{ color: C.cardHeading }}>Generate Blueprint</strong> above to propose a title, modules, lessons, assessments, and workbook structure for review.
          </div>
        )}
        {busy !== "blueprint" && bpEverGenerated && !bpValid && (
          <div className="rounded-lg p-3 text-sm mb-3"
               style={{ background: C.dangerBg, color: C.danger, border: "1px solid #FCA5A5" }}
               data-testid="ai-builder-blueprint-invalid">
            The last blueprint response was empty or invalid. Click <strong>Retry Blueprint Generation</strong> above to try again.
          </div>
        )}

        {/* Verification Required — every finding is shown; approval is
            blocked while unresolved blockers remain. */}
        {bpValid && verifItems.length > 0 && (
          <VerificationRequired
            courseId={courseId}
            items={verifItems}
            unresolvedBlockers={unresolvedBlockers}
            onChange={(c) => setCourse(c)}
          />
        )}

        {/* Blueprint content */}
        {bpValid && (
          <div className="text-sm space-y-4" style={{ color: C.cardBody }} data-testid="ai-builder-blueprint-content">
            {bp.mandatory_disclaimer && (
              <div className="rounded-lg p-3 text-xs"
                   style={{ background: "#F1F5F9", color: C.cardBody,
                            border: `1px solid ${C.cardBorder}` }}
                   data-testid="ai-builder-bp-disclaimer">
                <strong style={{ color: C.cardHeading }}>Required disclaimer:</strong> {bp.mandatory_disclaimer}
              </div>
            )}
            {bp.details_changed_at && (
              <div className="rounded-lg p-3 text-xs"
                   style={{ background: C.warnBg, color: C.warnText,
                            border: "1px solid #FCD34D" }}
                   data-testid="ai-builder-bp-outdated">
                Course details changed since this blueprint was generated. Click
                <strong> Regenerate Blueprint</strong> above to refresh it with the updated details.
              </div>
            )}
            {bp.summary && (
              <BpBlock title="Description" testid="ai-builder-bp-summary">{bp.summary}</BpBlock>
            )}
            {intake.learner_outcome && (
              <BpBlock title="Intended learner outcome" testid="ai-builder-bp-outcome">
                {intake.learner_outcome}
              </BpBlock>
            )}
            {(bp.learning_objectives || []).length > 0 && (
              <BpBlock title="Learning objectives" testid="ai-builder-bp-objectives">
                <ul className="list-disc pl-5 space-y-1">
                  {bp.learning_objectives.map((o, i) => <li key={i}>{o}</li>)}
                </ul>
              </BpBlock>
            )}
            {(bp.competencies || []).length > 0 && (
              <BpBlock title="Competencies" testid="ai-builder-bp-competencies">
                <ul className="list-disc pl-5 space-y-1">
                  {bp.competencies.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </BpBlock>
            )}
            {(bp.module_structure || []).length > 0 && (
              <BpBlock title="Module outline" testid="ai-builder-bp-modules">
                <ol className="list-decimal pl-5 space-y-2">
                  {bp.module_structure.map((m, i) => (
                    <li key={i}>
                      <span className="font-semibold" style={{ color: C.cardHeading }}>{m.title}</span>
                      {m.overview && <span> — {m.overview}</span>}
                      {(m.lesson_titles || []).length > 0 && (
                        <ul className="list-disc pl-5 mt-1" style={{ color: C.cardBody }}>
                          {m.lesson_titles.map((lt, j) => <li key={j}>{lt}</li>)}
                        </ul>
                      )}
                    </li>
                  ))}
                </ol>
              </BpBlock>
            )}
            {bp.assessment_plan && (
              <BpBlock title="Assessment plan" testid="ai-builder-bp-assessment">
                {bp.assessment_plan}
              </BpBlock>
            )}
            {bp.marketing_description && (
              <BpBlock title="Marketing description" testid="ai-builder-bp-marketing">
                {bp.marketing_description}
              </BpBlock>
            )}
            {bp.certificate_language && (
              <BpBlock title="Certificate language" testid="ai-builder-bp-certificate">
                {bp.certificate_language}
                {bp.certificate_language_status && (
                  <div className="text-xs mt-1"
                       style={{ color: C.warnText }}
                       data-testid="ai-builder-bp-certificate-status">
                    {bp.certificate_language_status}
                  </div>
                )}
              </BpBlock>
            )}
            <BpBlock title="Proposed duration" testid="ai-builder-bp-duration">
              <div>{String(bp.proposed_duration ?? "—")}</div>
              <div className="text-xs mt-1" style={{ color: C.warnText }}
                   data-testid="ai-builder-bp-duration-status">
                {bp.proposed_duration_label
                 || "Suggested duration — pending administrator verification."}
              </div>
            </BpBlock>
            <BpBlock title="Approved CE hours" testid="ai-builder-bp-ce-hours">
              <div>{Number(bp.ce_hours_approved || 0) || "—"}</div>
              <div className="text-xs mt-1" style={{ color: C.warnText }}
                   data-testid="ai-builder-bp-ce-hours-status">
                {bp.ce_hours_status || "Pending administrator verification."}
              </div>
            </BpBlock>
          </div>
        )}
      </section>

      {/* Stage 2 — Full content */}
      <section className="rounded-xl p-6 mb-6"
               style={{ background: "#FFFFFF", border: `1px solid ${C.cardBorder}`, color: C.cardBody }}
               data-testid="ai-builder-content-section">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div>
            <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: C.focus }}>Stage 2 · Full draft</div>
            <h2 className="text-lg mt-1" style={{ color: C.cardHeading }}>Modules, lessons, narration scripts</h2>
          </div>
          <button
            onClick={generateContent}
            disabled={!bpApproved || startingJob
                       || (fullDraftJob && ["queued","running"].includes(fullDraftJob.status))}
            className="rounded-lg px-3 py-2 text-sm font-semibold"
            style={{ background: bpApproved ? C.ctaBg : "#94A3B8",
                     color: C.ctaText, opacity: bpApproved ? 1 : 0.6,
                     cursor: bpApproved ? "pointer" : "not-allowed" }}
            data-testid="ai-builder-generate-content"
          >
            {fullDraftJob && ["queued","running"].includes(fullDraftJob.status)
              ? "Generating Course…"
              : fullDraftJob && fullDraftJob.status === "completed"
                ? "Full Draft Complete"
                : startingJob ? "Starting…" : "Generate Full Draft"}
          </button>
        </div>
        {!bpApproved && (
          <div className="text-xs" style={{ color: C.cardMuted }}>
            Approve the blueprint above before generating full content.
          </div>
        )}
        {fullDraftJob && (
          <FullDraftJobBanner
            job={fullDraftJob}
            onResume={resumeJob}
            courseId={courseId}
            starting={startingJob}
          />
        )}
      </section>

      {showEditIntake && (
        <EditIntakeDialog
          course={course}
          config={config}
          onClose={() => setShowEditIntake(false)}
          onSaved={(c) => { setCourse(c); setShowEditIntake(false); toast.success("Intake updated."); }}
        />
      )}
    </div>
  );
}

function BpBlock({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider font-bold mb-1" style={{ color: C.cardMuted }}>
        {title}
      </div>
      <div style={{ color: C.cardBody }}>{children}</div>
    </div>
  );
}

// ═════════════ Full-draft job progress banner ═════════════
function FullDraftJobBanner({ job, onResume, courseId, starting }) {
  const status = job?.status || "";
  const completed = Number(job?.completed_lessons || 0);
  const total = Number(job?.total_lessons || 0);
  const failed = (job?.failed_lesson_ids || []).length;
  const running = ["queued","running"].includes(status);
  const canResume = ["stale","failed","partial"].includes(status);
  const label =
    status === "queued"    ? "Queued"
    : status === "running" ? `Generating Module ${(job.current_module_index || 0)+1} — Lesson ${(job.current_lesson_index || 0)+1}`
    : status === "completed" ? "Full Draft Complete"
    : status === "partial"   ? "Partially Completed — one or more lessons failed"
    : status === "stale"     ? "Generation appears stalled — resume to continue"
    : status === "failed"    ? "Generation failed"
    : "";
  return (
    <div className="mt-3 rounded-lg p-3 text-sm"
         style={{ background: status === "completed" ? C.successBg
                                : status === "failed" ? C.dangerBg
                                : "#EFF6FF",
                  color: status === "completed" ? C.successText
                          : status === "failed" ? C.danger : "#1E40AF",
                  border: `1px solid ${status === "completed" ? "#86EFAC"
                                        : status === "failed" ? "#FCA5A5"
                                        : "#93C5FD"}` }}
         data-testid="ai-builder-fulldraft-job">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold" data-testid="ai-builder-fulldraft-status">{label}</div>
          {total > 0 && (
            <div className="text-xs mt-1">
              {completed} of {total} lessons complete
              {failed ? ` · ${failed} failed` : ""}
            </div>
          )}
        </div>
        <div className="flex gap-2">
          {canResume && (
            <button
              onClick={onResume}
              disabled={starting}
              className="rounded px-3 py-1 text-xs font-semibold"
              style={{ background: "#FFFFFF", color: C.cardHeading,
                       border: `1px solid ${C.cardBorderStrong}`,
                       opacity: starting ? 0.5 : 1 }}
              data-testid="ai-builder-fulldraft-resume"
            >
              {starting ? "Resuming…" : "Resume Generation"}
            </button>
          )}
          {completed > 0 && (
            <>
              <a
                href={`/kcis/admin/university/course-builder/${courseId}`}
                target="_blank" rel="noreferrer"
                className="rounded px-3 py-1 text-xs font-semibold"
                style={{ background: "#FFFFFF", color: C.cardHeading,
                         border: `1px solid ${C.cardBorderStrong}` }}
                data-testid="ai-builder-fulldraft-open-editor"
              >
                Advanced Course Editor
              </a>
              <div data-testid="ai-builder-fulldraft-preview-wrap">
                <PreviewAsStudentButton
                  courseId={courseId}
                  label="Preview as Student"
                  variant="ghost"
                  size="sm"
                  testIdSuffix="ai-builder-fulldraft"
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ═════════════ Verification Required ═════════════
function VerificationRequired({ courseId, items, unresolvedBlockers, onChange }) {
  const [pending, setPending] = useState({});   // itemId -> action label

  async function act(item, action) {
    if (pending[item.id]) return;
    setPending((p) => ({ ...p, [item.id]: action }));
    try {
      const { data } = await api.post(
        `${BASE}/${courseId}/verification/resolve`,
        { action, item_ids: [item.id] },
      );
      onChange && onChange(data.course);
      toast.success(
        action === "acknowledge" ? "Marked as pending verification."
        : action === "source"     ? "Marked as source-backed."
        : "Marked as removed from blueprint.",
      );
    } catch (e) {
      toast.error("Could not update that item. Please try again.");
    } finally {
      setPending((p) => { const q = { ...p }; delete q[item.id]; return q; });
    }
  }

  return (
    <div className="rounded-xl p-4 mt-4"
         style={{ background: unresolvedBlockers.length ? "#FEF2F2" : "#F0FDF4",
                  border: `1px solid ${unresolvedBlockers.length ? "#FCA5A5" : "#86EFAC"}` }}
         data-testid="ai-builder-verification-required">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold"
               style={{ color: unresolvedBlockers.length ? C.danger : C.successText }}>
            Verification Required
          </div>
          <h3 className="text-sm font-semibold mt-1"
              style={{ color: unresolvedBlockers.length ? C.danger : C.successText }}
              data-testid="ai-builder-verification-headline">
            {unresolvedBlockers.length
              ? `${unresolvedBlockers.length} item(s) block approval`
              : "All items resolved — approval unblocked"}
          </h3>
        </div>
      </div>
      <ul className="mt-3 space-y-2">
        {items.map((f) => {
          const status = f.acknowledged
            ? "Pending administrator verification"
            : f.sourced   ? "Source attached"
            : f.resolved  ? "Removed from blueprint"
            : "Unresolved";
          const resolved = !!(f.acknowledged || f.sourced || f.resolved);
          const busy = pending[f.id];
          return (
            <li key={f.id} className="rounded-lg p-3 text-sm"
                style={{ background: resolved ? "#FFFFFF" : "#FEE2E2",
                         border: `1px solid ${resolved ? "#E2E8F0" : "#FCA5A5"}` }}
                data-testid={`ai-builder-verification-item-${f.id}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-wide font-semibold"
                       style={{ color: C.cardMuted }}>
                    {f.category} · {f.severity} · {status}
                  </div>
                  <div className="mt-1" style={{ color: C.cardHeading }}
                       data-testid={`verification-item-text-${f.id}`}>{f.item}</div>
                  {f.suggestion && (
                    <div className="text-xs mt-1" style={{ color: C.cardBody }}>
                      {f.suggestion}
                    </div>
                  )}
                </div>
                {!resolved && (
                  <div className="flex flex-col gap-1 shrink-0">
                    <button
                      type="button"
                      onClick={() => act(f, "acknowledge")}
                      disabled={!!busy}
                      className="rounded px-2 py-1 text-[11px] font-semibold"
                      style={{ background: "#FFFFFF", color: C.cardHeading,
                               border: `1px solid ${C.cardBorderStrong}`,
                               opacity: busy ? 0.5 : 1 }}
                      data-testid={`verification-acknowledge-${f.id}`}
                    >
                      {busy === "acknowledge" ? "Saving…" : "Mark pending"}
                    </button>
                    <button
                      type="button"
                      onClick={() => act(f, "source")}
                      disabled={!!busy}
                      className="rounded px-2 py-1 text-[11px] font-semibold"
                      style={{ background: "#FFFFFF", color: C.cardHeading,
                               border: `1px solid ${C.cardBorderStrong}`,
                               opacity: busy ? 0.5 : 1 }}
                      data-testid={`verification-source-${f.id}`}
                    >
                      {busy === "source" ? "Saving…" : "I attached a source"}
                    </button>
                    <button
                      type="button"
                      onClick={() => act(f, "remove")}
                      disabled={!!busy}
                      className="rounded px-2 py-1 text-[11px] font-semibold"
                      style={{ background: "#FFFFFF", color: C.cardHeading,
                               border: `1px solid ${C.cardBorderStrong}`,
                               opacity: busy ? 0.5 : 1 }}
                      data-testid={`verification-remove-${f.id}`}
                    >
                      {busy === "remove" ? "Saving…" : "Remove claim"}
                    </button>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ═════════════ Edit intake dialog (correct type/length after creation) ═════════════
function EditIntakeDialog({ course, config, onClose, onSaved }) {
  const initial = course.intake || {};
  const [experienceType, setExperienceType] = useState(initial.experience_type || "");
  const [title, setTitle] = useState(initial.title || "");
  const [learnerOutcome, setLearnerOutcome] = useState(initial.learner_outcome || "");
  const [sourceInformation, setSourceInformation] = useState(initial.source_information || "");
  const [desiredLength, setDesiredLength] = useState(initial.desired_length || "custom");
  const [creditHours, setCreditHours] = useState(
    String(initial.credit_hours || initial.total_clock_hours || "")
  );
  const [instructorId, setInstructorId] = useState(initial.primary_instructor_id || "");
  const [advanced, setAdvanced] = useState(initial.advanced_options || {});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/kcis/admin/instructor-profiles")
      .then(({ data }) => setInstructors(normalizeInstructorList(data?.items)))
      .catch(() => {});
  }, []);

  const isCoaching = experienceType === "coaching_program";
  const isRegulated = experienceType === "certification_module"
                       || experienceType === "continuing_education";
  const creditHoursValue = parseFloat(creditHours);
  const creditHoursValid = Number.isFinite(creditHoursValue) && creditHoursValue > 0;

  async function save() {
    if (!experienceType || !title.trim() || learnerOutcome.trim().length < 8) {
      setErr("Experience Type, Working Title, and Learner Outcome are required.");
      return;
    }
    if (!creditHoursValid) {
      setErr("Credit Hours must be a positive whole or decimal number.");
      return;
    }
    setSaving(true); setErr("");
    try {
      const { data } = await api.patch(`${BASE}/intake/${course.id}`, {
        experience_type: experienceType,
        title: title.trim(),
        learner_outcome: learnerOutcome.trim(),
        source_information: sourceInformation,
        desired_length: desiredLength,
        credit_hours: creditHoursValue,
        primary_instructor_id: instructorId,
        advanced_options: advanced,
      });
      onSaved(data.course);
    } catch (e) {
      const s = e?.response?.status;
      const d = e?.response?.data?.detail;
      setErr((d && d.message) || `Save failed (HTTP ${s || "network"}).`);
    } finally { setSaving(false); }
  }

  const types = config?.experience_types || Object.keys(EXPERIENCE_LABELS);
  const lengths = config?.desired_lengths || Object.keys(LENGTH_LABELS);

  return (
    <div role="dialog" aria-modal="true"
         className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(15,23,42,0.72)" }}
         data-testid="ai-builder-edit-intake-dialog">
      <div className="w-full max-w-2xl rounded-2xl shadow-xl max-h-[92vh] overflow-y-auto"
           style={{ background: "#FFFFFF", color: C.cardBody, border: `1px solid ${C.cardBorder}` }}>
        <div className="p-5" style={{ borderBottom: `1px solid #E2E8F0` }}>
          <div className="text-[11px] uppercase tracking-wider font-bold" style={{ color: C.focus }}>Edit course details</div>
          <h2 className="text-lg font-display mt-1" style={{ color: C.cardHeading }}>Update this draft</h2>
          <p className="text-xs mt-1" style={{ color: C.cardMuted }}>
            Manual edits do not call the AI. Save Changes updates the same draft.
            {" "}Regenerate the blueprint separately when you&apos;re ready.
          </p>
        </div>
        <div className="p-5 space-y-5">
          <Field label="Experience Type *">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {types.map((t) => {
                const selected = experienceType === t;
                return (
                  <button
                    key={t} type="button"
                    onClick={() => setExperienceType(t)}
                    aria-pressed={selected}
                    className="text-left rounded-xl p-3 text-sm font-medium"
                    style={{
                      background: selected ? C.chipSelectedBg : C.chipUnselectedBg,
                      color: selected ? C.chipSelectedText : C.chipUnselectedText,
                      border: `2px solid ${selected ? C.chipSelectedBg : C.chipUnselectedBorder}`,
                    }}
                    data-testid={`edit-intake-type-${t}`}
                  >
                    {selected && <span aria-hidden="true" style={{ marginRight: 6 }}>✓</span>}
                    {EXPERIENCE_LABELS[t] || t}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Working Title *">
            <TextInput value={title} onChange={setTitle} testid="edit-intake-title" />
          </Field>
          <Field label="Learner Outcome *">
            <TextArea value={learnerOutcome} onChange={setLearnerOutcome} rows={3} testid="edit-intake-outcome" />
          </Field>
          <Field label="Source Information">
            <TextArea value={sourceInformation} onChange={setSourceInformation} rows={3} testid="edit-intake-source" />
          </Field>
          <Field label="Desired Length *">
            <select
              value={desiredLength}
              onChange={(e) => setDesiredLength(e.target.value)}
              className="w-full h-11 rounded-xl px-3 text-sm font-medium"
              style={{ background: "#FFFFFF", color: C.inputText,
                       border: `1px solid ${C.cardBorderStrong}` }}
              data-testid="edit-intake-length"
            >
              {lengths.map((l) => <option key={l} value={l}>{LENGTH_LABELS[l] || l}</option>)}
            </select>
          </Field>
          <Field label={isCoaching ? "Program Hours *" : "Credit Hours *"}>
            <input
              type="number" min="0.5" step="0.5"
              value={creditHours}
              onChange={(e) => setCreditHours(e.target.value)}
              className="w-full h-11 rounded-xl px-3 text-sm font-medium"
              style={{ background: "#FFFFFF", color: C.inputText,
                       border: `1px solid ${creditHoursValid ? C.cardBorderStrong : "#FCA5A5"}` }}
              data-testid="edit-intake-credit-hours"
            />
          </Field>
          <Field label="Instructor">
            <select
              value={instructorId}
              onChange={(e) => setInstructorId(e.target.value)}
              className="w-full h-11 rounded-xl px-3 text-sm font-medium"
              style={{ background: "#FFFFFF", color: C.inputText,
                       border: `1px solid ${C.cardBorderStrong}` }}
              data-testid="edit-intake-instructor"
            >
              <option value="">— Select an instructor —</option>
              {instructors.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name} — {i.voice_ready ? "Voice ready" : "Voice not connected"}
                </option>
              ))}
            </select>
          </Field>
          <div style={{ paddingTop: 8, borderTop: `1px solid #E2E8F0` }}>
            <button
              type="button" onClick={() => setAdvancedOpen((v) => !v)}
              className="text-xs font-semibold" style={{ color: C.focus }}
              data-testid="edit-intake-advanced-toggle"
            >
              {advancedOpen ? "▾" : "▸"} Advanced Options
            </button>
            {advancedOpen && (
              <div className="mt-3 space-y-4" data-testid="edit-intake-advanced-panel">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {["audience","prerequisites","academic_level","tone","passing_score",
                     "delivery_format","citation_requirements","accessibility_preferences"].map((k) => (
                    <label key={k} className="block">
                      <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.cardLabel }}>
                        {k.replaceAll("_"," ")}
                      </span>
                      <input
                        type="text" value={advanced[k] || ""}
                        onChange={(e) => setAdvanced({ ...advanced, [k]: e.target.value })}
                        className="mt-1 w-full h-9 rounded-lg px-2 text-sm"
                        style={{ background: "#FFFFFF", color: C.inputText,
                                 border: `1px solid ${C.cardBorderStrong}` }}
                      />
                    </label>
                  ))}
                </div>
                {isRegulated && (
                  <>
                    <LicenseCategoryPicker
                      value={advanced.license_categories || []}
                      onChange={(next) => setAdvanced({ ...advanced, license_categories: next })}
                    />
                    <RegulatorySources
                      value={advanced.regulatory_sources || []}
                      onChange={(next) => setAdvanced({ ...advanced, regulatory_sources: next })}
                    />
                  </>
                )}
              </div>
            )}
          </div>
          {err && (
            <div className="rounded-lg p-3 text-sm"
                 style={{ background: C.dangerBg, color: C.danger, border: "1px solid #FCA5A5" }}
                 data-testid="edit-intake-error">{err}</div>
          )}
        </div>
        <div className="p-5 flex justify-end gap-2" style={{ borderTop: `1px solid #E2E8F0` }}>
          <button
            type="button" onClick={onClose} disabled={saving}
            className="rounded-lg px-4 py-2 text-sm font-semibold"
            style={{ background: "#FFFFFF", color: C.cardHeading,
                     border: `1px solid ${C.cardBorderStrong}`,
                     opacity: saving ? 0.5 : 1 }}
            data-testid="edit-intake-cancel"
          >
            Cancel
          </button>
          <button
            type="button" onClick={save} disabled={saving || !creditHoursValid}
            className="rounded-lg px-4 py-2 text-sm font-semibold"
            style={{ background: C.ctaBg, color: C.ctaText,
                     opacity: (saving || !creditHoursValid) ? 0.6 : 1 }}
            data-testid="edit-intake-save"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═════════════ Course Built Summary + Post-Build Actions ═════════════
// Displays the "where the course lives" summary card the moment a
// course has any generated lessons OR any pending selected assets.
// Provides the visible action buttons: Complete Missing Assets,
// Preview as Student, Manage Course, Advanced Course Editor, Edit
// Course Details, Publish Course. Every button has explicit dark-
// on-light or light-on-dark contrast — no invisible labels.
function CourseBuiltSummary({ course, job, onCompleteMissing, onPublish,
                                completing, publishing, courseId }) {
  const intake = course?.intake || {};
  const assets = course?.course_assets || {};
  const selected = assets.selected_assets || {};
  const lessons  = course?.lessons || [];
  const modules  = course?.modules || [];
  const generatedLessons = lessons.filter(
    (l) => ["generated", "approved", "edited"].includes(l.status));
  const pendingKeys = Object.entries(selected)
    .filter(([, v]) => v.selected
      && ["pending_generation", "failed", "needs_visual_review",
          "voiceover_pending"].includes(v.status))
    .map(([k]) => k);
  const generatedCount = Object.values(selected)
    .filter((v) => v.selected && v.status === "generated").length;
  const selectedCount = Object.values(selected)
    .filter((v) => v.selected).length;
  const readinessPct = selectedCount
    ? Math.round((generatedCount / selectedCount) * 100) : 0;
  const isPublished = course?.status === "published";
  const anyGenerated = generatedLessons.length > 0;

  // Do not render before there is anything to summarise.
  if (!anyGenerated && !pendingKeys.length) return null;

  const missingLabel = pendingKeys.length
    ? `Complete ${pendingKeys.length} missing asset${pendingKeys.length > 1 ? "s" : ""}`
    : "Complete Missing Assets";

  return (
    <section className="rounded-xl p-5 mb-5"
             style={{ background: "#F1F5F9", border: "1px solid #CBD5E1",
                      color: "#0F172A" }}
             data-testid="ai-builder-course-built-summary">
      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-widest font-bold"
               style={{ color: "#7C3AED" }}>
            {isPublished ? "Published" : "Draft — not visible to students"}
          </div>
          <h3 className="text-lg font-semibold mt-1"
              data-testid="ai-builder-course-built-title">
            {intake.title || "Untitled course"}
          </h3>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
            <SumItem label="Modules"     value={modules.length}
                      testid="summary-modules" />
            <SumItem label="Lessons"     value={lessons.length}
                      testid="summary-lessons" />
            <SumItem label="Credit hrs"  value={intake.credit_hours || 0}
                      testid="summary-credit-hours" />
            <SumItem label="Instructor"  value={intake.primary_instructor_id || "—"}
                      testid="summary-instructor" />
            <SumItem label="Program"     value={intake.program_id || "—"}
                      testid="summary-program" />
            <SumItem label="Catalog"
                      value={course?.catalog_section
                             || _catalogSectionLabel(intake.experience_type)}
                      testid="summary-catalog" />
            <SumItem label="Voiceover"
                      value={selected.narration_scripts?.status === "generated"
                             && lessons.some((l) => l.narration?.cache_id)
                             ? "Ready" : "Voiceover Pending"}
                      testid="summary-voiceover" />
            <SumItem label="Visuals"
                      value={selected.slides?.status === "generated"
                             ? "Text-only (Needs Visual Review)"
                             : "Pending"}
                      testid="summary-visuals" />
            <SumItem label="Readiness"
                      value={`${readinessPct}%`}
                      testid="summary-readiness" />
          </div>
          {pendingKeys.length > 0 && (
            <div className="mt-3 rounded-lg p-3 text-xs"
                 style={{ background: "#FEF3C7", color: "#78350F",
                          border: "1px solid #FDE68A" }}
                 data-testid="summary-pending-list">
              <strong>Pending:</strong> {pendingKeys.join(", ")}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2 sm:min-w-[260px]">
          {pendingKeys.length > 0 && (
            <>
              <button
                type="button"
                onClick={onCompleteMissing}
                disabled={completing}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-center"
                style={{ background: completing ? "#94A3B8" : "#7C3AED",
                         color: "#FFFFFF", border: "1px solid #6D28D9",
                         cursor: completing ? "not-allowed" : "pointer",
                         opacity: completing ? 0.7 : 1 }}
                data-testid="ai-builder-complete-missing-assets"
              >
                {completing ? "Working…" : missingLabel}
              </button>
              <div className="text-[11px] text-slate-600 text-center"
                   data-testid="ai-builder-complete-missing-help">
                Finish the workbook, assessments, visuals, voiceover and
                other selected course assets without regenerating completed
                lessons.
              </div>
            </>
          )}
          <div className="flex flex-wrap gap-2 justify-center">
            <div data-testid="summary-preview-as-student-wrap">
              <PreviewAsStudentButton
                courseId={courseId}
                label="Preview as Student"
                variant="ghost"
                size="sm"
                testIdSuffix="summary"
              />
            </div>
            <a
              href={`/kcis/admin/university/course-builder/${courseId}`}
              target="_blank" rel="noreferrer"
              className="rounded-lg px-3 py-2 text-xs font-semibold text-center"
              style={{ background: "#FFFFFF", color: "#0F172A",
                       border: "1px solid #CBD5E1" }}
              data-testid="ai-builder-summary-advanced-editor"
            >
              Advanced Course Editor
            </a>
            <a
              href={`/kcis/admin/university/enrollments?course=${courseId}`}
              target="_blank" rel="noreferrer"
              className="rounded-lg px-3 py-2 text-xs font-semibold text-center"
              style={{ background: "#FFFFFF", color: "#0F172A",
                       border: "1px solid #CBD5E1" }}
              data-testid="ai-builder-summary-manage-course"
            >
              Manage Course
            </a>
            <button
              type="button"
              onClick={onPublish}
              disabled={publishing || isPublished || pendingKeys.length > 0}
              className="rounded-lg px-3 py-2 text-xs font-semibold text-center"
              style={{ background: (publishing || pendingKeys.length > 0)
                         ? "#94A3B8" : "#10B981",
                       color: "#0F172A",
                       border: "1px solid #059669",
                       cursor: (publishing || isPublished || pendingKeys.length > 0)
                         ? "not-allowed" : "pointer",
                       opacity: (publishing || isPublished
                                  || pendingKeys.length > 0) ? 0.7 : 1 }}
              title={pendingKeys.length > 0
                ? "Resolve pending assets first — click Complete Missing Assets."
                : isPublished ? "Already published"
                              : "Approve and Publish this course"}
              data-testid="ai-builder-publish-course"
            >
              {isPublished ? "Published"
                : publishing ? "Publishing…"
                : "Publish Course"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function SumItem({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div className="text-sm font-medium text-slate-900 truncate">
        {value}
      </div>
    </div>
  );
}

function _catalogSectionLabel(exp) {
  return ({
    university_course: "University catalog",
    certification_module: "Certification programs",
    continuing_education: "Continuing Education",
    workshop: "Workshops",
    coaching_program: "Coaching Programs",
  })[exp] || "Continuing Education";
}

